/**
 * @fileoverview Lifecycle command mutation hook.
 * @description What Deactivate and Reactivate share, in one place so a dialog can't get it wrong:
 * the Idempotency-Key for the action on the user (kept while the outcome is unknown, dropped after
 * a definite answer; see useIdempotencyKey), the cache refresh after a success, and the dialog's
 * completion callback. The completion callback is an option of the mutation itself rather than a
 * callback passed to each `mutate` call: TanStack Query skips the latter when the component that
 * called `mutate` has unmounted, which would lose the confirmation (and the dialog's close) for a
 * command the server went on to apply. Order on success: the key is settled, the caches refresh,
 * then the callback runs.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useIdempotencyKey } from '@/features/users/hooks/useIdempotencyKey'
import { idempotencyScope } from '@/features/users/utils/idempotency-keys.utils'
import { refreshAfterLifecycleChange } from '@/features/users/utils/users-cache.utils'

import type { IdempotentAction } from '@/features/users/utils/idempotency-keys.utils'
import type { ApiError } from '@/shared/types/http.types'

/** What a lifecycle command's variables must carry. */
interface LifecycleVariables {
  /** The user the command acts on. */
  userId: string
}

interface LifecycleMutationOptions<TResult, TVariables extends LifecycleVariables> {
  /** Which lifecycle command this is (it scopes the Idempotency-Key). */
  action: IdempotentAction
  /** Sends the command with the Idempotency-Key for it. */
  send: (variables: TVariables, idempotencyKey: string) => Promise<TResult>
  /** Called with the result after a success, even when the dialog has unmounted since. */
  onSuccess?: ((result: TResult) => void) | undefined
}

/**
 * @description Runs a lifecycle command with its idempotency key, cache refresh and completion
 * callback.
 * @param {LifecycleMutationOptions<TResult, TVariables>} options - The command, how to send it, and
 * what to do after a success.
 * @param {IdempotentAction} options.action - Which lifecycle command this is.
 * @param {(variables: TVariables, idempotencyKey: string) => Promise<TResult>} options.send -
 * Sends the command.
 * @param {(result: TResult) => void} [options.onSuccess] - Called with the result after a success.
 * @returns {import('@tanstack/react-query').UseMutationResult<TResult, ApiError, TVariables>} The
 * mutation object (mutate, isPending, error, ...).
 */
export function useLifecycleMutation<TResult, TVariables extends LifecycleVariables>({
  action,
  send,
  onSuccess,
}: LifecycleMutationOptions<TResult, TVariables>) {
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()

  return useMutation<TResult, ApiError, TVariables>({
    /**
     * @description Sends the command under the key for this action on this user.
     * @param {TVariables} variables - The user and the command's request.
     * @returns {Promise<TResult>} The command's result.
     */
    mutationFn: (variables) =>
      send(variables, idempotency.keyFor(idempotencyScope(action, variables.userId))),
    /**
     * @description Settles the action's idempotency key (the same action later is a new command),
     * refreshes the lists and summaries the command changed, then reports the result.
     * @param {TResult} result - The command's result.
     * @param {TVariables} variables - The user and the command's request.
     */
    onSuccess: (result, variables) => {
      idempotency.settleSuccess(idempotencyScope(action, variables.userId))
      refreshAfterLifecycleChange(queryClient)
      onSuccess?.(result)
    },
    /**
     * @description Keeps the idempotency key when the outcome is unknown (no response, a timeout, a
     * 408 or a 5xx) so a retry is a replay, and drops it after a definite refusal.
     * @param {ApiError} error - The failed attempt's error.
     * @param {TVariables} variables - The user and the command's request.
     */
    onError: (error, variables) => {
      idempotency.settleFailure(idempotencyScope(action, variables.userId), error)
    },
  })
}
